import React, { useState, useEffect, useRef } from "react"; // 1. Added useRef
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import ReactMarkdown from "react-markdown";
import {
  Copy,
  Download,
  RefreshCw,
  Zap,
  FileText,
  Edit3,
  CheckCircle,
  AlignLeft,
  Trash2,
  Maximize2
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import auth from "../../../firebase.init";

const ArticleRewriteTool = () => {
  // ==========================================
  // 1. STATE MANAGEMENT
  // ==========================================
  const [user, loadingAuth] = useAuthState(auth);
  const [loading, setLoading] = useState(true);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const outputRef = useRef(null); // 2. Ref for the output container

  // User Data & Credits
  const [credits, setCredits] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);

  // Form State
  const [originalText, setOriginalText] = useState("");
  const [rewriteStyle, setRewriteStyle] = useState("Professional");
  const [targetAudience, setTargetAudience] = useState("");
  
  // Output State
  const [rewrittenText, setRewrittenText] = useState("");

  // Options
  const styleOptions = [
    { value: "Professional", label: "Professional & Formal" },
    { value: "Simple", label: "Simple & Clear (Grade 8)" },
    { value: "SEO", label: "SEO Optimized (Keyword Rich)" },
    { value: "Creative", label: "Creative & Engaging" },
    { value: "Academic", label: "Academic & Scholarly" },
    { value: "Persuasive", label: "Persuasive & Salesy" },
    { value: "Casual", label: "Casual & Friendly" },
  ];

  // ==========================================
  // 2. BACKEND / CREDIT LOGIC (PRESERVED)
  // ==========================================

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`https://allyoutube-01-02d59d78beb5.herokuapp.com/api/gemini-keys`);
      if (response.data && response.data.success && response.data.data) {
        const keys = response.data.data.map((keyObj) => keyObj.key);
        setApiKeys(keys);
        if (keys.length === 0) setError("No API keys available.");
      } else {
        setError("Failed to load API keys");
      }
    } catch (err) {
      console.error("Error fetching API keys:", err);
      setError("Error loading API keys");
    } finally {
      setLoadingKeys(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (loadingAuth) return;
      if (!user) {
        setError("User not logged in");
        setLoading(false);
        setLoadingKeys(false);
        return;
      }
      try {
        await fetchApiKeys();
        const userResponse = await axios.get(`https://allyoutube-01-02d59d78beb5.herokuapp.com/users`, {
          params: { userEmail: user.email },
        });
        if (userResponse.data && userResponse.data.length > 0) {
          setCredits(userResponse.data[0].currentBalance);
        } else {
          setError("User data not found");
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user, loadingAuth]);

  const getRandomApiKey = () => {
    if (apiKeys.length === 0) throw new Error("No API keys available");
    return apiKeys[Math.floor(Math.random() * apiKeys.length)];
  };

  // ==========================================
  // 3. AI GENERATION LOGIC
  // ==========================================

  const constructPrompt = () => {
    return `You are an expert Editor and Content Rewriter.
    
    Task: Rewrite the following text.
    Target Audience: ${targetAudience || "General Reader"}
    Rewrite Style: ${rewriteStyle}
    
    Rules:
    1. Preserve the original meaning and core facts.
    2. Maintain formatting (keep headings, bullet points, and paragraph structure).
    3. Improve flow, clarity, and grammar.
    4. Use Markdown for formatting (Bold important terms, use H2/H3 for sections, use bullet points).
    5. Do not include preambles like "Here is the rewritten text". Just output the content.
    
    Input Text:
    "${originalText}"`;
  };

  const generateRewrite = async () => {
    if (!originalText.trim()) {
      toast.error("Please enter text to rewrite.");
      return;
    }
    if (originalText.length < 10) {
      toast.error("Text is too short to rewrite.");
      return;
    }
    if (credits < 1) {
      setError("Insufficient credits. You need at least 1 credit.");
      return;
    }

    setGenerating(true);
    setError("");
    setRewrittenText("");

    try {
      const deductResponse = await axios.post(
        `https://allyoutube-01-02d59d78beb5.herokuapp.com/api/deduct-credit`,
        {
          userEmail: user.email,
          amount: 1,
        }
      );

      if (!deductResponse.data.success) {
        throw new Error("Failed to deduct credits");
      }
      setCredits((prev) => prev - 1);

      const MODEL_ID = "gemini-2.5-flash-lite";
      const apiKey = getRandomApiKey();
      const API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${MODEL_ID}:generateContent?key=${apiKey}`;
      
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ role: "user", parts: [{ text: constructPrompt() }] }],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 4096,
          },
        }),
      });

      if (!response.ok) throw new Error("AI Generation Failed");

      const data = await response.json();
      const generatedText = data.candidates?.[0]?.content?.parts?.[0]?.text;

      if (generatedText) {
        setRewrittenText(generatedText);
        toast.success("Article rewritten successfully!");
      } else {
        throw new Error("Empty response from AI");
      }

    } catch (err) {
      console.error("Generation Error:", err);
      toast.error(err.message);
      setError(err.message);

      try {
        await axios.post(`https://allyoutube-01-02d59d78beb5.herokuapp.com/api/refund-credit`, {
          userEmail: user.email,
          amount: 1,
        });
        setCredits((prev) => prev + 1);
        toast("Credits refunded due to error.");
      } catch (refundErr) {
        console.error("Refund failed:", refundErr);
      }
    } finally {
      setGenerating(false);
    }
  };

  // ==========================================
  // 4. UTILS (UPDATED COPY LOGIC)
  // ==========================================

  // Fixed: Copies Rich Text (HTML) for Word/Docs and Plain Text for Notepad
  const handleCopy = () => {
    if (!rewrittenText || !outputRef.current) return;

    try {
      // Get the rendered HTML (Rich Text)
      const htmlContent = outputRef.current.innerHTML;
      // Get the rendered Text (Clean Text without markdown symbols)
      const textContent = outputRef.current.innerText;

      const blobHtml = new Blob([htmlContent], { type: "text/html" });
      const blobText = new Blob([textContent], { type: "text/plain" });

      const data = [new ClipboardItem({
        "text/html": blobHtml,
        "text/plain": blobText,
      })];

      navigator.clipboard.write(data).then(() => {
        toast.success("Copied formatted text!");
      });
    } catch (err) {
      // Fallback for browsers with stricter clipboard policies
      navigator.clipboard.writeText(outputRef.current.innerText).then(() => {
        toast.success("Copied text!");
      });
    }
  };

  const downloadText = () => {
    if (!outputRef.current) return;
    // Download the Clean Text (not the Markdown source)
    const element = document.createElement("a");
    const file = new Blob([outputRef.current.innerText], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `rewritten-article-${Date.now()}.txt`;
    document.body.appendChild(element);
    element.click();
  };

  const getWordCount = (text) => {
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  const handleClear = () => {
    if(window.confirm("Are you sure you want to clear the input?")) {
      setOriginalText("");
    }
  }

  // ==========================================
  // 5. RENDER
  // ==========================================

  if (loadingAuth || loading || loadingKeys) {
    return (
      <div className="loader-container">
        <div className="spinner"></div>
        <p>Loading Rewriter...</p>
        <style jsx>{`
          .loader-container { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; background: #f8fafc; color: #64748b; }
          .spinner { width: 40px; height: 40px; border: 3px solid #cbd5e1; border-top-color: #3b82f6; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 1rem; }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="auth-wall">
        <div className="auth-card">
          <h2>Log in Required</h2>
          <p>Please log in to use the Article Rewriter.</p>
          <button onClick={() => window.location.href = "/login"}>Go to Login</button>
        </div>
        <style jsx>{`
          .auth-wall { display: flex; align-items: center; justify-content: center; height: 100vh; background: #f1f5f9; }
          .auth-card { background: white; padding: 2rem; border-radius: 12px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); text-align: center; max-width: 400px; width: 90%; }
          .auth-card h2 { margin-bottom: 1rem; color: #1e293b; }
          .auth-card button { background: #3b82f6; color: white; border: none; padding: 0.75rem 1.5rem; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
          .auth-card button:hover { background: #2563eb; }
        `}</style>
      </div>
    );
  }

  return (
    <div className="app-container">
    
      
      {/* HEADER */}
      <header className="app-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => window.history.back()}>&larr; Back</button>
          <div className="title-block">
            <h1>Article Rewrite Tool</h1>
            <span className="badge">AI-Powered</span>
          </div>
        </div>
        <div className="credits-badge">
          <Zap size={16} fill="#eab308" color="#eab308" />
          <span>{credits} Credits</span>
          {credits < 5 && <button onClick={() => window.location.href = "/user/select-package"}>Buy More</button>}
        </div>
      </header>

      <main className="editor-workspace">
        
        {/* SETTINGS BAR */}
        <div className="settings-bar">
          <div className="setting-group">
            <label><Edit3 size={14} /> Rewrite Style</label>
            <select value={rewriteStyle} onChange={(e) => setRewriteStyle(e.target.value)}>
              {styleOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          
          <div className="setting-group fluid">
            <label><Maximize2 size={14} /> Target Audience (Optional)</label>
            <input 
              type="text" 
              value={targetAudience} 
              onChange={(e) => setTargetAudience(e.target.value)}
              placeholder="e.g. Beginners, Industry Experts, Students" 
            />
          </div>

          <button 
            className="action-btn" 
            onClick={generateRewrite}
            disabled={generating || credits < 1 || !originalText.trim()}
          >
            {generating ? (
              <>
                <RefreshCw className="spin" size={18} /> Rewriting...
              </>
            ) : (
              <>
                <RefreshCw size={18} /> Rewrite Article
              </>
            )}
          </button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {/* EDITOR SPLIT VIEW */}
        <div className="split-view">
          
          {/* LEFT: INPUT */}
          <div className="pane input-pane">
            <div className="pane-header">
              <span className="pane-title"><AlignLeft size={16} /> Original Text</span>
              <div className="pane-actions">
                 <span className="word-count">{getWordCount(originalText)} words</span>
                 {originalText && (
                   <button className="icon-btn danger" onClick={handleClear} title="Clear Text">
                     <Trash2 size={16} />
                   </button>
                 )}
              </div>
            </div>
            <textarea
              className="editor-textarea"
              value={originalText}
              onChange={(e) => setOriginalText(e.target.value)}
              placeholder="Paste your article, paragraph, or content here to rewrite..."
            ></textarea>
          </div>

          {/* RIGHT: OUTPUT (RICH TEXT) */}
          <div className="pane output-pane">
            <div className="pane-header">
              <span className="pane-title">
                <CheckCircle size={16} className={rewrittenText ? "text-green" : ""} /> 
                Rewritten Version
              </span>
              <div className="pane-actions">
                {rewrittenText && (
                  <>
                    <span className="word-count">{getWordCount(rewrittenText)} words</span>
                    {/* Using new handleCopy */}
                    <button className="icon-btn" onClick={handleCopy} title="Copy Formatted Text">
                      <Copy size={16} />
                    </button>
                    <button className="icon-btn" onClick={downloadText} title="Download">
                      <Download size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>
            
            <div className="editor-output">
              {generating ? (
                <div className="loading-state">
                  <div className="pulse-ring"></div>
                  <p>AI is analyzing and rewriting your content...</p>
                </div>
              ) : rewrittenText ? (
                /* RICH TEXT RENDERER WITH REF */
                <div className="rich-text-content" ref={outputRef}>
                  <ReactMarkdown>{rewrittenText}</ReactMarkdown>
                </div>
              ) : (
                <div className="empty-state">
                  <FileText size={48} opacity={0.1} />
                  <p>Rewritten content will appear here.</p>
                </div>
              )}
            </div>
          </div>

        </div>
      </main>

      {/* STYLES */}
      <style jsx>{`
        /* GLOBAL */
        .app-container {
          font-family: 'Inter', system-ui, sans-serif;
          background-color: #f8fafc;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }

        /* HEADER */
        .app-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 2rem;
          background: white;
          border-bottom: 1px solid #e2e8f0;
          height: 70px;
        }

        .header-left { display: flex; align-items: center; gap: 1.5rem; }
        
        .title-block h1 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
          display: inline-block;
        }

        .badge {
          background: #eff6ff;
          color: #3b82f6;
          font-size: 0.7rem;
          padding: 2px 8px;
          border-radius: 12px;
          margin-left: 8px;
          font-weight: 600;
          vertical-align: middle;
        }

        .back-btn {
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          font-weight: 500;
        }

        .credits-badge {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: #fffbeb;
          color: #92400e;
          padding: 0.5rem 1rem;
          border-radius: 999px;
          border: 1px solid #fcd34d;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .credits-badge button {
          background: #d97706;
          color: white;
          border: none;
          padding: 0.25rem 0.75rem;
          border-radius: 4px;
          font-size: 0.75rem;
          cursor: pointer;
          margin-left: 0.5rem;
        }

        /* WORKSPACE */
        .editor-workspace {
          max-width: 1400px;
          margin: 0 auto;
          width: 100%;
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          height: calc(100vh - 70px);
        }

        /* SETTINGS BAR */
        .settings-bar {
          display: flex;
          align-items: flex-end;
          gap: 1.5rem;
          background: white;
          padding: 1.25rem;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          border: 1px solid #e2e8f0;
          flex-wrap: wrap;
        }

        .setting-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .setting-group.fluid { flex: 1; min-width: 200px; }

        .setting-group label {
          font-size: 0.8rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        select, input {
          padding: 0.7rem 1rem;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          font-size: 0.95rem;
          min-width: 200px;
          outline: none;
          transition: border-color 0.2s;
        }
        
        select:focus, input:focus { border-color: #3b82f6; }

        .action-btn {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 0.7rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          height: 42px;
          transition: background 0.2s;
        }

        .action-btn:hover:not(:disabled) { background: #2563eb; }
        .action-btn:disabled { background: #94a3b8; cursor: not-allowed; }

        .spin { animation: spin 1s linear infinite; }

        /* SPLIT VIEW EDITOR */
        .split-view {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          flex: 1;
          min-height: 0;
        }

        .pane {
          display: flex;
          flex-direction: column;
          background: white;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          overflow: hidden;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
        }

        .pane-header {
          padding: 1rem;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .pane-title {
          font-weight: 600;
          color: #334155;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .text-green { color: #16a34a; }

        .pane-actions {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .word-count {
          font-size: 0.75rem;
          color: #94a3b8;
          font-weight: 500;
          margin-right: 8px;
        }

        .icon-btn {
          background: white;
          border: 1px solid #e2e8f0;
          padding: 6px;
          border-radius: 6px;
          color: #64748b;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .icon-btn:hover { border-color: #3b82f6; color: #3b82f6; }
        .icon-btn.danger:hover { border-color: #ef4444; color: #ef4444; }

        .editor-textarea {
          flex: 1;
          padding: 1.5rem;
          border: none;
          resize: none;
          outline: none;
          font-family: inherit;
          font-size: 1rem;
          line-height: 1.6;
          color: #334155;
        }

        .editor-output {
          flex: 1;
          padding: 1.5rem;
          overflow-y: auto;
          background-color: #ffffff;
        }

        /* RICH TEXT STYLING */
        .rich-text-content {
          font-family: inherit;
          font-size: 1rem;
          line-height: 1.7;
          color: #1e293b;
        }
        
        /* Typography specific to Rich Text */
        .rich-text-content :global(h1),
        .rich-text-content :global(h2),
        .rich-text-content :global(h3) {
          color: #0f172a;
          font-weight: 700;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }

        .rich-text-content :global(h1) { font-size: 1.75rem; border-bottom: 2px solid #f1f5f9; padding-bottom: 0.5rem; }
        .rich-text-content :global(h2) { font-size: 1.4rem; }
        .rich-text-content :global(h3) { font-size: 1.2rem; }
        
        .rich-text-content :global(p) { margin-bottom: 1rem; }
        
        .rich-text-content :global(ul), 
        .rich-text-content :global(ol) {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }

        .rich-text-content :global(li) { margin-bottom: 0.5rem; }
        
        .rich-text-content :global(strong) { color: #0f172a; font-weight: 600; }
        
        .rich-text-content :global(blockquote) {
          border-left: 4px solid #3b82f6;
          padding-left: 1rem;
          margin-left: 0;
          font-style: italic;
          color: #64748b;
          background: #f8fafc;
          padding: 1rem;
          border-radius: 4px;
        }

        .empty-state {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #cbd5e1;
        }
        
        .empty-state p { margin-top: 10px; font-weight: 500; }

        .loading-state {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #64748b;
        }

        .pulse-ring {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 4px solid #3b82f6;
          animation: pulse-ring 1.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
        }

        @keyframes pulse-ring {
          0% { transform: scale(0.5); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: scale(1.2); opacity: 0; }
        }

        .error-banner {
          background: #fee2e2;
          color: #b91c1c;
          padding: 10px 15px;
          border-radius: 8px;
          font-size: 0.9rem;
          border: 1px solid #fecaca;
        }

        @media (max-width: 900px) {
          .editor-workspace { height: auto; }
          .split-view { grid-template-columns: 1fr; }
          .pane { min-height: 400px; }
          .settings-bar { flex-direction: column; align-items: stretch; }
          .action-btn { width: 100%; justify-content: center; }
        }
      `}</style>
    </div>
  );
};

export default ArticleRewriteTool;